import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits
} from '../../../../types';
import {
  FetchBillingInfoCacheKey,
  FetchBillingInfoGqlQuery,
  FetchBillingInfosCacheKey,
  FetchBillingInfosGqlQuery,
  FetchBillingInfosLimit,
  FetchBillingInfosPage,
  FetchBillingInfosSort,
  FetchFinBillingInfosFilters
} from '../../billingInfosTypes';
import { FetchCacheBillingInfosQueryResponse } from '../../queries/fetchCacheBillingInfos.query';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_BILLING_INFOS_FILTERS,
  INITIAL_BILLING_INFOS_LIMIT,
  INITIAL_BILLING_INFOS_PAGE,
  INITIAL_BILLING_INFOS_SORT
} from '../../billingInfosConstants';

interface BillingInfosWithPrefetchItemOptions {
  fetchItemCacheKey: FetchBillingInfoCacheKey;
  fetchItemQuery: FetchBillingInfoGqlQuery;
}

interface BillingInfosWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

interface BillingInfosDefaultOptions {
  action?: string;
  cacheKey: FetchBillingInfosCacheKey;
  query: FetchBillingInfosGqlQuery;
  initialFilters?: FetchFinBillingInfosFilters;
  initialSort?: FetchBillingInfosSort;
  initialPage?: FetchBillingInfosPage;
  initialLimit?: FetchBillingInfosLimit;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
}

type BillingInfosOptions = BillingInfosDefaultOptions &
  (
    | BillingInfosWithPrefetchItemOptions
    | BillingInfosWithoutPrefetchItemOptions
  );

const scope = 'billingInfos';

function useCachePaginatedBillingInfos<
  FetchBillingInfosType = FetchCacheBillingInfosQueryResponse
>({
  action,
  cacheKey,
  initialFilters = INITIAL_BILLING_INFOS_FILTERS,
  initialSort = INITIAL_BILLING_INFOS_SORT,
  initialPage = INITIAL_BILLING_INFOS_PAGE,
  initialLimit = INITIAL_BILLING_INFOS_LIMIT,
  fetchItemCacheKey,
  fetchItemQuery,
  query,
  source = FetchCacheItemsSources.DB,
  trackTotalHits,
  options = {}
}: BillingInfosOptions) {
  const {
    data,
    items,
    itemsError,
    itemsErrorMessage,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    updateItemCache,
    updateItemsCache,
    clearItemsFilters,
    filterItems,
    changeItemsFilters,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    prefetchItem
  } = useCacheIndexQuery<FetchBillingInfosType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    scope,
    query,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    fetchItemCacheKey,
    fetchItemQuery,
    options,
    source,
    trackTotalHits
  });

  return {
    billingInfosData: data,
    billingInfos: items,
    billingInfosError: itemsError,
    billingInfosErrorMessage: itemsErrorMessage,
    billingInfosTotalCount: itemsTotalCount,
    billingInfosFetched: isFetched,
    billingInfosLoading: isLoading,
    billingInfosIsPlaceholderData: isPlaceholderData,
    billingInfosFilters: currentFilters,
    billingInfosSort: currentSort,
    billingInfosPage: currentPage,
    billingInfosLimit: currentLimit,
    updateBillingInfoCache: updateItemCache,
    updateBillingInfosCache: updateItemsCache,
    filterBillingInfos: filterItems,
    changeBillingInfosFilters: changeItemsFilters,
    clearBillingInfosFilters: clearItemsFilters,
    sortBillingInfos: sortItems,
    paginateBillingInfos: paginateItems,
    limitBillingInfos: limitItems,
    prefetchBillingInfos: prefetchItems,
    prefetchBillingInfo: prefetchItem
  };
}

export default useCachePaginatedBillingInfos;
